if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.mql = window.matchMedia('(min-width: 750px)');
        this.elements = {};
      }

      connectedCallback() {
        this.cacheElements();
        this.initThumbnails();
        this.scheduleViewerInit();
      }

      scheduleViewerInit() {
        const run = () => {
          this.cacheElements();
          this.initViewerArrows();
        };

        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', () => {
            requestAnimationFrame(() => requestAnimationFrame(run));
          });
        } else {
          requestAnimationFrame(() => requestAnimationFrame(run));
        }
      }

      cacheElements() {
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
      }

      revealActiveSlide(activeMedia) {
        if (!activeMedia) return;
        activeMedia.classList.remove('scroll-trigger--offscreen');
        activeMedia.classList.add('scroll-trigger--cancel');

        if (!activeMedia._trsRevealObserver) {
          activeMedia._trsRevealObserver = new MutationObserver(() => {
            if (
              activeMedia.classList.contains('is-active') &&
              activeMedia.classList.contains('scroll-trigger--offscreen')
            ) {
              activeMedia.classList.remove('scroll-trigger--offscreen');
              activeMedia.classList.add('scroll-trigger--cancel');
            }
          });
          activeMedia._trsRevealObserver.observe(activeMedia, {
            attributes: true,
            attributeFilter: ['class'],
          });
        }
      }

      initThumbnails() {
        if (!this.elements.thumbnails || !this.elements.viewer || this._thumbsInitialized) return;
        this._thumbsInitialized = true;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`li[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('li[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('li[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');
        this.revealActiveSlide(activeMedia);

        this.syncViewerControls?.();

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        if (activeThumbnail) {
          this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
        }
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }

      initViewerArrows() {
        const viewer = this.elements.viewer;
        if (!viewer || this._arrowsInitialized) return;

        const layout = this.dataset.desktopLayout || '';
        if (!layout.includes('thumbnail')) return;

        let prevBtn = viewer.querySelector('.slider-button--prev');
        let nextBtn = viewer.querySelector('.slider-button--next');
        if (!prevBtn || !nextBtn) return;

        this._arrowsInitialized = true;

        const replaceButton = (button) => {
          const clone = button.cloneNode(true);
          button.parentNode.replaceChild(clone, button);
          return clone;
        };
        prevBtn = replaceButton(prevBtn);
        nextBtn = replaceButton(nextBtn);

        const getSlides = () => Array.from(viewer.querySelectorAll('li.product__media-item[data-media-id]'));

        this.syncViewerControls = () => {
          if (this._syncingViewerControls || !this.mql.matches) return;
          this._syncingViewerControls = true;
          try {
            const slides = getSlides();
            const active = slides.find((slide) => slide.classList.contains('is-active')) || slides[0];
            const idx = Math.max(0, slides.indexOf(active));
            const current = viewer.querySelector('.slider-counter--current');
            const total = viewer.querySelector('.slider-counter--total');

            if (current) current.textContent = String(idx + 1);
            if (total) total.textContent = String(slides.length);

            prevBtn.toggleAttribute('disabled', idx <= 0);
            nextBtn.toggleAttribute('disabled', idx >= slides.length - 1);
          } finally {
            this._syncingViewerControls = false;
          }
        };

        const navigateViewer = (direction) => {
          if (!this.mql.matches) return;
          const slides = getSlides();
          const active = slides.find((slide) => slide.classList.contains('is-active'));
          let idx = slides.indexOf(active);
          if (idx < 0) idx = 0;
          const targetIdx = direction === 'next' ? idx + 1 : idx - 1;
          if (targetIdx < 0 || targetIdx >= slides.length) return;
          this.setActiveMedia(slides[targetIdx].dataset.mediaId, false);
        };

        const handleArrowClick = (event, direction) => {
          if (!this.mql.matches) return;
          event.stopImmediatePropagation();
          event.preventDefault();
          navigateViewer(direction);
        };

        prevBtn.addEventListener('click', (event) => handleArrowClick(event, 'prev'), true);
        nextBtn.addEventListener('click', (event) => handleArrowClick(event, 'next'), true);

        const handleViewerPointer = (event) => {
          if (!this.mql.matches) return;
          /*
            Edge-click navigation must not steal taps on gallery chrome:
            zoom (top-left), wishlist (top-right), arrows, or the lightbox
            toggle. Synthetic .click() also lands at clientX=0 (left edge),
            which previously changed slides instead of opening zoom.
          */
          if (
            event.target.closest(
              'button, a, [role="button"], [data-trs-gallery-zoom], [data-trs-gallery-wishlist], .trs-gallery-wishlist, .Vtl-WishlistIcon__ProductPageWrapper, .product__media-toggle, modal-opener'
            )
          ) {
            return;
          }
          /* Ignore non-pointer / zero-coordinate synthetic events */
          if (!event.clientX && !event.clientY) return;

          const rect = viewer.getBoundingClientRect();
          const x = event.clientX - rect.left;
          const edge = Math.min(72, rect.width * 0.14);
          if (x > edge && x < rect.width - edge) return;

          const slides = getSlides();
          const active = slides.find((slide) => slide.classList.contains('is-active'));
          const idx = Math.max(0, slides.indexOf(active));
          if (x <= edge && idx <= 0) return;
          if (x >= rect.width - edge && idx >= slides.length - 1) return;

          event.stopImmediatePropagation();
          event.preventDefault();
          navigateViewer(x <= edge ? 'prev' : 'next');
        };

        if (this._viewerPointerHandler) {
          viewer.removeEventListener('click', this._viewerPointerHandler, true);
        }
        this._viewerPointerHandler = handleViewerPointer;
        viewer.addEventListener('click', handleViewerPointer, true);

        getSlides().forEach((slide) => this.revealActiveSlide(slide));
        const activeSlide = getSlides().find((slide) => slide.classList.contains('is-active')) || getSlides()[0];
        this.revealActiveSlide(activeSlide);
        this.syncViewerControls();

        getSlides().forEach((slide) => {
          new MutationObserver(() => this.syncViewerControls()).observe(slide, {
            attributes: true,
            attributeFilter: ['class'],
          });
        });

        this.mql.addEventListener('change', this.syncViewerControls);
      }
    }
  );

  const refreshMediaGalleries = (forceReinit = false) => {
    document.querySelectorAll('media-gallery').forEach((gallery) => {
      if (forceReinit) gallery._arrowsInitialized = false;
      gallery.cacheElements();
      const viewer = gallery.elements.viewer;
      if (!viewer) return;

      viewer.querySelectorAll('li.product__media-item[data-media-id]').forEach((slide) => {
        gallery.revealActiveSlide(slide);
      });
      gallery.syncViewerControls?.();

      if (forceReinit) gallery.scheduleViewerInit();
    });
  };

  if (!window.__trsMediaGalleryLifecycle) {
    window.__trsMediaGalleryLifecycle = true;
    window.addEventListener('pageshow', (event) => {
      refreshMediaGalleries(event.persisted);
    });
    document.addEventListener('DOMContentLoaded', () => {
      requestAnimationFrame(() => requestAnimationFrame(() => refreshMediaGalleries(false)));
    });
  }
}
